import type { PlanungAssignment, PlanungAssignmentInput, PlanungOrderLink } from "@/lib/montageplanung";
import {
  addDays,
  diffDays,
  enumerateDays,
  isoWeekday,
  isNonWorkDay,
  parseIso,
  startOfWeekMonday,
  toIso,
} from "./dateUtils";

/** Zwischenablage für Kopieren/Einfügen von Einsätzen (nur Frontend-Session). */
export interface PlanungAssignmentClipboard {
  source_assignment_id: number;
  title: string;
  color: string | null;
  date_from: string;
  date_to: string;
  is_fixed: boolean;
  is_blocker: boolean;
  is_company_event: boolean;
  orders: PlanungOrderLink[];
  note: string | null;
  /** Serie: beim verknüpften Einfügen keine Projekte mitkopieren. */
  is_series?: boolean;
}

export function assignmentToClipboard(assignment: PlanungAssignment): PlanungAssignmentClipboard {
  return {
    source_assignment_id: assignment.id,
    title: assignment.title,
    color: assignment.color,
    date_from: assignment.date_from.slice(0, 10),
    date_to: assignment.date_to.slice(0, 10),
    is_fixed: assignment.is_fixed,
    is_blocker: assignment.is_blocker ?? false,
    is_company_event: assignment.is_company_event ?? false,
    orders: (assignment.orders ?? []).map((o) => ({
      order_id: o.order_id,
      order_label: o.order_label,
    })),
    note: assignment.note,
    is_series: !!assignment.is_series,
  };
}

function snapToNextWorkDay(date: Date, nonWorkWeekdays?: number[] | null): Date {
  let current = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  for (let i = 0; i < 14; i++) {
    if (!isNonWorkDay(current, nonWorkWeekdays)) return current;
    current = addDays(current, 1);
  }
  return date;
}

/** Ersten und letzten Arbeitstag im Kalenderzeitraum (keine deaktivierten Tage als Grenzen). */
function clipRangeToWorkDayBounds(
  from: Date,
  to: Date,
  nonWorkWeekdays?: number[] | null,
): { from: Date; to: Date } {
  const workDays = enumerateDays(from, to).filter((d) => !isNonWorkDay(d, nonWorkWeekdays));
  if (workDays.length === 0) {
    const snapped = snapToNextWorkDay(from, nonWorkWeekdays);
    return { from: snapped, to: snapped };
  }
  return { from: workDays[0]!, to: workDays[workDays.length - 1]! };
}

/**
 * Berechnet den Zielzeitraum beim Einfügen:
 * - 1–2 Tage: Start am Klickpunkt (Arbeitstag), gleiche Kalenderlänge
 * - 3+ Tage: gleiche Wochentage in der Zielwoche (z. B. Mo–Fr bleibt Mo–Fr)
 * - Keine deaktivierten Tage als Start/Ende; bei Mehr-Tages-Einsätzen nur Arbeitstage im Block
 */
export function computePasteDateRange(
  origFromIso: string,
  origToIso: string,
  anchorIso: string,
  nonWorkWeekdays?: number[] | null,
): { date_from: string; date_to: string } {
  const from = parseIso(origFromIso.slice(0, 10));
  const to = parseIso(origToIso.slice(0, 10));
  const anchor = snapToNextWorkDay(parseIso(anchorIso.slice(0, 10)), nonWorkWeekdays);
  const durationDays = diffDays(from, to) + 1;

  let newFrom: Date;
  let newTo: Date;

  if (durationDays <= 2) {
    newFrom = anchor;
    newTo = addDays(anchor, durationDays - 1);
    if (isNonWorkDay(newFrom, nonWorkWeekdays)) {
      newFrom = snapToNextWorkDay(newFrom, nonWorkWeekdays);
      newTo = addDays(newFrom, durationDays - 1);
    }
    if (durationDays === 1) {
      newTo = newFrom;
    } else if (isNonWorkDay(newTo, nonWorkWeekdays)) {
      // Zweitägig: Endtag auf nächsten Arbeitstag nach Start, falls Endtag deaktiviert
      let end = addDays(newFrom, 1);
      if (isNonWorkDay(end, nonWorkWeekdays)) {
        end = snapToNextWorkDay(end, nonWorkWeekdays);
      }
      newTo = end;
    }
    return { date_from: toIso(newFrom), date_to: toIso(newTo) };
  }

  const weekMonday = startOfWeekMonday(anchor);
  const startOffset = isoWeekday(from) - 1;
  newFrom = addDays(weekMonday, startOffset);
  newTo = addDays(newFrom, durationDays - 1);

  const clipped = clipRangeToWorkDayBounds(newFrom, newTo, nonWorkWeekdays);
  return { date_from: toIso(clipped.from), date_to: toIso(clipped.to) };
}

/** Gemerkte Vorgabe: eingefügte Kopien mit dem Ursprung verknüpfen. */
export const PASTE_LINK_STORAGE_KEY = "mp-paste-linked";

export function readPasteLinkedPreference(): boolean {
  try {
    return window.localStorage.getItem(PASTE_LINK_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function writePasteLinkedPreference(linked: boolean): void {
  try {
    window.localStorage.setItem(PASTE_LINK_STORAGE_KEY, linked ? "1" : "0");
  } catch {
    // Einstellung gilt dann nur für die aktuelle Sitzung.
  }
}

export function clipboardToAssignmentInput(
  clipboard: PlanungAssignmentClipboard,
  rowId: number,
  anchorDate: string,
  nonWorkWeekdays?: number[] | null,
  options?: { link?: boolean },
): PlanungAssignmentInput {
  const { date_from, date_to } = computePasteDateRange(
    clipboard.date_from,
    clipboard.date_to,
    anchorDate,
    nonWorkWeekdays,
  );
  const link = options?.link ?? true;
  const seriesLink = link && !!clipboard.is_series;
  return {
    row_id: rowId,
    title: clipboard.title,
    color: clipboard.color,
    date_from,
    date_to,
    is_fixed: clipboard.is_fixed,
    is_blocker: clipboard.is_blocker,
    is_company_event: clipboard.is_company_event,
    orders: seriesLink ? [] : clipboard.orders,
    note: clipboard.note,
    ...(link ? { link_to_assignment_id: clipboard.source_assignment_id } : {}),
  };
}

export function clipboardSummary(clipboard: PlanungAssignmentClipboard): string {
  const from = clipboard.date_from.slice(0, 10);
  const to = clipboard.date_to.slice(0, 10);
  const range = from === to ? from : `${from} – ${to}`;
  return `${clipboard.title} (${range})`;
}
